import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FormField } from '../shared/FormField';
import { NativeSelect } from '../shared/NativeSelect';
import { SearchInput } from '../shared/SearchInput';
import { SECTION_LABEL_CLASS } from '../../constants/ui';
import { formatAmount } from '../../utils/formatters';
import { MONEDAS, aplanarAgrupadores, rutaAgrupador } from '../../utils/agrupadores';
import type { Agrupador, NombreItemAgrupable } from '../../types';

interface Props {
  nombres: NombreItemAgrupable[];
  agrupadores: Agrupador[];
  canEdit: boolean;
  isAsignando?: boolean;
  onAsignar: (nombres: string[], agrupadorId: number | null) => Promise<void>;
}

/**
 * Nombres distintos de ítems de presupuesto (normalizados). Asociar un nombre a
 * un agrupador una vez alcanza: todo ítem actual o futuro con ese nombre cae ahí.
 */
export function AsociarNombresTable({ nombres, agrupadores, canEdit, isAsignando = false, onAsignar }: Props) {
  const [search, setSearch] = useState('');
  const [filtro, setFiltro] = useState<'todos' | 'sin_agrupar' | 'agrupados'>('sin_agrupar');
  const [seleccionados, setSeleccionados] = useState<Set<string>>(new Set());
  const [destino, setDestino] = useState('');

  const opciones = useMemo(() => aplanarAgrupadores(agrupadores), [agrupadores]);

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    return nombres.filter((n) => {
      if (filtro === 'sin_agrupar' && n.agrupadorId !== null) return false;
      if (filtro === 'agrupados' && n.agrupadorId === null) return false;
      if (!q) return true;
      return n.variantes.some((v) => v.toLowerCase().includes(q)) || n.nombreNormalizado.includes(q);
    });
  }, [nombres, search, filtro]);

  const todosSeleccionados = filtrados.length > 0 && filtrados.every((n) => seleccionados.has(n.nombreNormalizado));

  const toggle = (clave: string) =>
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(clave)) next.delete(clave);
      else next.add(clave);
      return next;
    });

  const toggleTodos = () =>
    setSeleccionados(todosSeleccionados ? new Set() : new Set(filtrados.map((n) => n.nombreNormalizado)));

  async function asignarSeleccionados(agrupadorId: number | null) {
    const elegidos = nombres.filter((n) => seleccionados.has(n.nombreNormalizado)).map((n) => n.nombre);
    if (elegidos.length === 0) return;
    await onAsignar(elegidos, agrupadorId);
    setSeleccionados(new Set());
  }

  return (
    <div>
      <div className="px-5 py-4 border-b border-gray-100 space-y-3">
        <div className="flex gap-3 items-end flex-wrap">
          <FormField label="Mostrar" className="w-44">
            <NativeSelect value={filtro} onChange={(e) => setFiltro(e.target.value as typeof filtro)} aria-label="Mostrar">
              <option value="sin_agrupar">Sin agrupar</option>
              <option value="agrupados">Agrupados</option>
              <option value="todos">Todos</option>
            </NativeSelect>
          </FormField>
          <FormField label="Buscar" className="flex-1 min-w-48">
            <SearchInput placeholder="Buscar nombre de ítem..." value={search} onChange={setSearch} />
          </FormField>
        </div>

        {canEdit && seleccionados.size > 0 && (
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-primary/30 bg-primary/5 p-3">
            <p className="text-sm font-medium text-gray-900 self-center">
              {seleccionados.size} {seleccionados.size === 1 ? 'nombre seleccionado' : 'nombres seleccionados'}
            </p>
            <FormField label="Asociar a" className="min-w-48 flex-1">
              <NativeSelect value={destino} onChange={(e) => setDestino(e.target.value)} aria-label="Agrupador destino">
                <option value="">Elegir agrupador...</option>
                {opciones.map((o) => (
                  <option key={o.id} value={String(o.id)}>
                    {'  '.repeat(o.profundidad)}{o.nombre}
                  </option>
                ))}
              </NativeSelect>
            </FormField>
            <Button
              size="sm"
              className="h-9 rounded-lg"
              disabled={!destino || isAsignando}
              onClick={() => asignarSeleccionados(Number(destino))}
            >
              Asociar
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-9 rounded-lg"
              disabled={isAsignando}
              onClick={() => asignarSeleccionados(null)}
            >
              Quitar agrupador
            </Button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              {canEdit && (
                <TableHead className="w-10">
                  <Checkbox checked={todosSeleccionados} onCheckedChange={toggleTodos} aria-label="Seleccionar todos" />
                </TableHead>
              )}
              <TableHead className={SECTION_LABEL_CLASS}>Nombre del ítem</TableHead>
              <TableHead className={SECTION_LABEL_CLASS}>Ítems</TableHead>
              <TableHead className={SECTION_LABEL_CLASS}>Pendiente de pago</TableHead>
              <TableHead className={SECTION_LABEL_CLASS}>Agrupador</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtrados.length === 0 && (
              <TableRow>
                <TableCell colSpan={canEdit ? 5 : 4} className="text-center text-muted-foreground py-10">
                  No hay nombres para los filtros seleccionados.
                </TableCell>
              </TableRow>
            )}
            {filtrados.map((n) => {
              const saldos = MONEDAS.filter((m) => n.saldoPendiente[m] > 0);
              return (
                <TableRow key={n.nombreNormalizado}>
                  {canEdit && (
                    <TableCell>
                      <Checkbox
                        checked={seleccionados.has(n.nombreNormalizado)}
                        onCheckedChange={() => toggle(n.nombreNormalizado)}
                        aria-label={`Seleccionar ${n.nombre}`}
                      />
                    </TableCell>
                  )}
                  <TableCell className="min-w-[14rem] max-w-md whitespace-normal break-words">
                    <p className="text-sm font-medium text-gray-900">{n.nombre}</p>
                    {n.variantes.length > 1 && (
                      <p className="text-xs text-muted-foreground">
                        También: {n.variantes.filter((v) => v !== n.nombre).join(' · ')}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-sm tabular-nums text-muted-foreground whitespace-nowrap">
                    {n.cantidadItems}
                    {n.enPlantillas && <Badge variant="outline" className="ml-2 text-[10px] font-normal">plantilla</Badge>}
                  </TableCell>
                  <TableCell className="text-sm tabular-nums whitespace-nowrap">
                    {saldos.length === 0
                      ? <span className="text-muted-foreground">—</span>
                      : saldos.map((m) => (
                        <p key={m} className="font-semibold text-slate-700">{m} $ {formatAmount(n.saldoPendiente[m], m)}</p>
                      ))}
                  </TableCell>
                  <TableCell className="min-w-[12rem]">
                    {canEdit ? (
                      <NativeSelect
                        value={n.agrupadorId === null ? '' : String(n.agrupadorId)}
                        disabled={isAsignando}
                        aria-label={`Agrupador de ${n.nombre}`}
                        onChange={(e) => onAsignar([n.nombre], e.target.value === '' ? null : Number(e.target.value))}
                      >
                        <option value="">Sin agrupar</option>
                        {opciones.map((o) => (
                          <option key={o.id} value={String(o.id)}>
                            {'  '.repeat(o.profundidad)}{o.nombre}
                          </option>
                        ))}
                      </NativeSelect>
                    ) : n.agrupadorId === null ? (
                      <span className="text-sm text-muted-foreground">Sin agrupar</span>
                    ) : (
                      <Badge variant="outline">{rutaAgrupador(agrupadores, n.agrupadorId)}</Badge>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
